// Every call to the Compass API: the session cookie, plus the X-Compass header the server requires on writes (CSRF).

export class ApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}
/** 401: the session is gone. The app switches to the sign-in page; nobody needs to show this one. */
export class SignedOut extends ApiError {}

let onSignedOut = () => {};
export const setSignedOutHandler = (f: () => void) => { onSignedOut = f; };
/** Show the sign-in page now (after Sign out), the same way a 401 does. */
export const signedOut = () => onSignedOut();

export async function api<T = unknown>(path: string, opts: { method?: string; body?: unknown } = {}): Promise<T> {
  const headers: Record<string, string> = { "X-Compass": "1" };
  let body: BodyInit | undefined;
  if (opts.body instanceof FormData) body = opts.body;
  else if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }
  const r = await fetch(path, { method: opts.method ?? "GET", headers, body, credentials: "same-origin" });
  if (r.status === 401) {
    onSignedOut();
    throw new SignedOut("signed out", 401);
  }
  const data: unknown = (r.headers.get("content-type") ?? "").includes("json") ? await r.json() : await r.text();
  if (!r.ok) {
    const d = (data as { detail?: unknown } | null)?.detail;
    throw new ApiError(d ? (typeof d === "string" ? d : JSON.stringify(d)) : r.statusText, r.status);
  }
  return data as T;
}

export const post = <T = unknown>(path: string, body?: unknown) => api<T>(path, { method: "POST", body });
export const patch = <T = unknown>(path: string, body?: unknown) => api<T>(path, { method: "PATCH", body });
export const put = <T = unknown>(path: string, body?: unknown) => api<T>(path, { method: "PUT", body });
export const del = <T = unknown>(path: string) => api<T>(path, { method: "DELETE" });
